/**
 * Add Child Task Dialog
 *
 * Dialog for adding a new child task to a parent task.
 * Supports task-type-specific options that appear when a type is selected.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Loader2 } from 'lucide-react';
import type { SqlSyncEntityOption } from '../../lib/api';

type TaskMode = 'sql-sync-entity' | 'sql-sync-metadata' | 'field-sync';

// Task-type-specific options
interface TaskOptions {
  // SQL Sync Entity options
  detectChangesFirst?: boolean;
  // SQL Sync Metadata options
  includePreview?: boolean;
  // Field Sync options
  maxRecords?: number;
}

interface AddChildDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (name: string, taskMode: TaskMode, entityType?: string, options?: TaskOptions) => Promise<void>;
  isAdding: boolean;
  sqlSyncEntities: SqlSyncEntityOption[];
  fieldSyncEntities: SqlSyncEntityOption[];
}

export function AddChildDialog({
  open,
  onOpenChange,
  onAdd,
  isAdding,
  sqlSyncEntities,
  fieldSyncEntities,
}: AddChildDialogProps) {
  const [name, setName] = useState('');
  const [taskMode, setTaskMode] = useState<TaskMode>('sql-sync-entity');
  const [entityType, setEntityType] = useState('');
  // Task-type-specific options with defaults
  const [detectChangesFirst, setDetectChangesFirst] = useState(true);
  const [includePreview, setIncludePreview] = useState(false);
  const [maxRecords, setMaxRecords] = useState<string>('');

  const handleAdd = async () => {
    const options: TaskOptions = {};
    if (taskMode === 'sql-sync-entity') {
      options.detectChangesFirst = detectChangesFirst;
    } else if (taskMode === 'sql-sync-metadata') {
      options.includePreview = includePreview;
    } else if (taskMode === 'field-sync') {
      if (maxRecords) {
        options.maxRecords = parseInt(maxRecords, 10);
      }
    }
    await onAdd(name, taskMode, entityType, options);
    // Reset form on success
    resetForm();
  };

  const resetForm = () => {
    setName('');
    setEntityType('');
    setDetectChangesFirst(true);
    setIncludePreview(false);
    setMaxRecords('');
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  const isValid = name.trim()
    && ((taskMode !== 'sql-sync-entity' && taskMode !== 'field-sync') || entityType)
    && (taskMode !== 'sql-sync-metadata' || !includePreview || entityType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Child Task</DialogTitle>
          <DialogDescription>
            Create a child task that runs as part of this parent.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="childName">Task Name</Label>
            <Input
              id="childName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Sync Series"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="childTaskMode">Task Type</Label>
            <Select
              value={taskMode}
              onValueChange={(value) => setTaskMode(value as TaskMode)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sql-sync-entity">SQL Sync Entity</SelectItem>
                <SelectItem value="sql-sync-metadata">SQL Sync Metadata Refresh</SelectItem>
                <SelectItem value="field-sync">Field Sync</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* SQL Sync Entity options */}
          {taskMode === 'sql-sync-entity' && (
            <div className="space-y-4 rounded-md border p-3 bg-muted/30">
              <div className="space-y-2">
                <Label htmlFor="childEntityType">Entity Type</Label>
                <Select
                  value={entityType}
                  onValueChange={setEntityType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {sqlSyncEntities.map((entity) => (
                      <SelectItem key={entity.value} value={entity.value}>
                        {entity.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="detectChanges" className="text-sm">Detect Changes First</Label>
                  <p className="text-xs text-muted-foreground">
                    Run incremental scan before syncing
                  </p>
                </div>
                <Switch
                  id="detectChanges"
                  checked={detectChangesFirst}
                  onCheckedChange={setDetectChangesFirst}
                />
              </div>
            </div>
          )}

          {/* SQL Sync Metadata options */}
          {taskMode === 'sql-sync-metadata' && (
            <div className="space-y-4 rounded-md border p-3 bg-muted/30">
              <div className="space-y-2">
                <Label htmlFor="metadataEntityType">Entity Type (optional)</Label>
                <Select
                  value={entityType}
                  onValueChange={setEntityType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All entities" />
                  </SelectTrigger>
                  <SelectContent>
                    {sqlSyncEntities.map((entity) => (
                      <SelectItem key={entity.value} value={entity.value}>
                        {entity.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="includePreview" className="text-sm">Include Preview Scan</Label>
                  <p className="text-xs text-muted-foreground">
                    Requires a specific entity. Leave off for routine all-entity refreshes.
                  </p>
                </div>
                <Switch
                  id="includePreview"
                  checked={includePreview}
                  onCheckedChange={setIncludePreview}
                />
              </div>
            </div>
          )}

          {/* Field Sync options */}
          {taskMode === 'field-sync' && (
            <div className="space-y-4 rounded-md border p-3 bg-muted/30">
              <div className="space-y-2">
                <Label htmlFor="fieldSyncEntityType">Entity Type</Label>
                <Select
                  value={entityType}
                  onValueChange={setEntityType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldSyncEntities.map((entity) => (
                      <SelectItem key={entity.value} value={entity.value}>
                        {entity.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxRecords" className="text-sm">Max Records (optional)</Label>
                <Input
                  id="maxRecords"
                  type="number"
                  value={maxRecords}
                  onChange={(e) => setMaxRecords(e.target.value)}
                  placeholder="Leave empty for all records"
                />
                <p className="text-xs text-muted-foreground">
                  Limit the number of records to process (useful for testing)
                </p>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={isAdding || !isValid}
          >
            {isAdding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
